import React, { useEffect, useRef, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { Check, Mail, X } from 'lucide-react';
import { emailSnapshotLetter } from '../services/api';
import { blobToBase64 } from '../services/pagePdf';
import './SnapshotLetter.css';

/**
 * "Email me this letter" — posts the snapshot to a parent as a PDF.
 *
 * The address is prefilled from the signed-in account and left editable, so
 * the same letter can go to a teacher or a second parent. Sending is handled
 * by `POST /snapshot/email/`, which attaches the page exactly as the parent
 * sees it: the client typesets the letter and sends its capture, so what
 * arrives is what is on screen.
 */

interface SendLetterDialogProps {
  open: boolean;
  onClose: () => void;
  childId?: string;
  grade?: string;
  childName: string;
  /** Renders the snapshot page itself to a PDF — the attachment that is sent. */
  onCapturePdf: () => Promise<Blob>;
}

type Status = 'idle' | 'sending' | 'sent' | 'error';

/** Enough to catch a typo. The mail server is the real authority. */
const looksLikeEmail = (value: string) => /^[^@\s,;]+@[^@\s,;]+\.[A-Za-z]{2,}$/.test(value.trim());

/** The backend's own words where it has them, ours where it does not. */
const errorMessage = (err: any): string => {
  const code = err?.response?.data?.error;
  if (code === 'email_not_configured') {
    return 'Emailing is not switched on yet. You can still use Save as PDF, and keep the file.';
  }
  if (code === 'email_delivery_failed') {
    return 'The mail server would not take the letter. Please try again in a moment.';
  }
  return err?.response?.data?.detail || err?.message || 'The letter could not be sent.';
};

const SendLetterDialog: React.FC<SendLetterDialogProps> = ({
  open,
  onClose,
  childId,
  grade,
  childName,
  onCapturePdf,
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const firstName = childName.split(' ')[0];

  // Open with the account's own address, and forget anything typed last time.
  useEffect(() => {
    if (!open) return;
    setEmail(getAuth().currentUser?.email || '');
    setStatus('idle');
    setMessage('');
    const focus = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(focus);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const send = async () => {
    if (!childId || !grade) {
      setStatus('error');
      setMessage('This letter cannot be emailed from here yet.');
      return;
    }
    setStatus('sending');
    setMessage('');
    try {
      const blob = await onCapturePdf();
      const pagePdf = await blobToBase64(blob);
      const result = await emailSnapshotLetter(childId, grade, email, pagePdf);
      setStatus('sent');
      setMessage(result.sent_to || email.trim());
    } catch (err: any) {
      setStatus('error');
      setMessage(errorMessage(err));
    }
  };

  const canSend = looksLikeEmail(email) && status !== 'sending';

  return (
    <div
      className="sl-mail"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sl-mail-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sl-mail__card">
        <button type="button" className="sl-mail__close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>

        {status === 'sent' ? (
          <>
            <span className="sl-mail__icon sl-mail__icon--done" aria-hidden="true">
              <Check size={20} />
            </span>
            <h2 className="sl-mail__title" id="sl-mail-title">
              On its way
            </h2>
            <p className="sl-mail__text">
              {firstName}'s letter is going to <strong>{message}</strong> as a PDF. Give it a
              minute, and check the spam folder if it has not arrived.
            </p>
            <div className="sl-mail__actions">
              <button type="button" className="sl-btn sl-btn--primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="sl-mail__icon" aria-hidden="true">
              <Mail size={20} />
            </span>
            <h2 className="sl-mail__title" id="sl-mail-title">
              Email this letter
            </h2>
            <p className="sl-mail__text">
              We will send {firstName}'s snapshot as a PDF, exactly as you see it on this page —
              yours to keep, print, or forward to a teacher.
            </p>

            <label className="sl-mail__label" htmlFor="sl-mail-address">
              Send to
            </label>
            <input
              id="sl-mail-address"
              ref={inputRef}
              className="sl-mail__input"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              placeholder="you@example.com"
              disabled={status === 'sending'}
              onChange={(event) => {
                setEmail(event.target.value);
                if (status === 'error') {
                  setStatus('idle');
                  setMessage('');
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && canSend) send();
              }}
            />

            {status === 'error' && <p className="sl-mail__error">{message}</p>}

            <div className="sl-mail__actions">
              <button
                type="button"
                className="sl-btn"
                onClick={onClose}
                disabled={status === 'sending'}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sl-btn sl-btn--primary"
                onClick={send}
                disabled={!canSend}
              >
                {status === 'sending' ? 'Preparing the page…' : 'Send it'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SendLetterDialog;
